/**
 * The sale header that sits above a catalogue — Browse and Live Now.
 *
 * 🔴 WHY THIS EXISTS. Jim, 2026-09-11, pointing at an incumbent's auction page:
 * *"You see how auction information stays at the top of the page? Adding this
 * to the website on the Browse and Live Now pages would look good."*
 *
 * He is right, and it is a hole in OUR product rather than a competitor's
 * feature. An open sale has no landing page — `/auctions/:slug` redirects to
 * Browse — so Browse *is* the sale page for anything a bidder can bid in. The
 * Sales LIST has carried a full summary card all along (`.w-sale`), and it
 * vanished the moment a bidder clicked into the lots: Browse showed one line of
 * grey text, and Live Now showed a pill and a title. The two screens a bidder
 * lives in had the least sale information anywhere in the product.
 *
 * COMPACT ON PURPOSE. Jim: *"We can try compact first."* A bidder came here for
 * the lots, so this answers "when, where, on what terms" in a band and gets out
 * of the way rather than pushing the catalogue below the fold. The incumbent
 * spends roughly a third of a screen on the equivalent.
 *
 * ONE BUTTON, NOT FOUR. The reference splits Description / Terms / Removal
 * Times / Preview across four icons. `SaleTermsModal` already contains all four
 * — it was built for the Sales list and the results page — so this opens that.
 * Four buttons onto one modal would be four ways to say the same thing.
 *
 * 🔴 IT PRINTS ONLY WHAT THE HOUSE PUBLISHES. `hide_ends_at` is a real flag
 * enforced in SQL, and the rules for what may be shown live in
 * `@auctisync/api-client` (`saleClosingBeginsAt`, `previewWindow`, `pickupLine`)
 * rather than here — the phone will want the same answers the day it grows a
 * header of its own, and a rule that lives in a component is a rule that will
 * be re-derived slightly differently somewhere else.
 *
 * ⚠️ THIS SHOWS A PLACE AND LEAVES COLLECTION TIMES TO THE MODAL. That was
 * once because there were no times to show; since 2026-09-18 the auction form
 * writes dated collection times, and this stays a place because the band is
 * already dense — the modal is where collection is read in full.
 *
 * 🔴 EVERY TIME IN THIS BAND IS THE SALE'S, NAMED. Jim, 2026-09-18: times
 * *"should always show the auction's local time"*. The live countdown is exempt
 * because it is a DURATION — "closes in 2h 14m" is the same everywhere and
 * needs no zone.
 */
import {
  remainingPickupWindows,
  parsePickupWindows,
  pickupLine,
  previewWindow,
  premiumPercent,
  saleClosingBeginsAt,
  type SaleHeader as SaleHeaderRow,
} from '@auctisync/api-client';
import { formatInZone, resolveTimeZone, type CalendarEvent } from '@auctisync/shared';

import { AddToCalendar } from '../AddToCalendar';
import { useMemo, useState } from 'react';

import { previewLabel } from './saleHeaderFormat';
import { SaleTermsModal } from '../SaleTermsModal';

export interface SaleHeaderProps {
  sale: SaleHeaderRow;
  houseName: string;
  /**
   * The server's clock READER, not a number — `useServerClock()` hands back a
   * function so a ticking component can re-read it without a re-render per
   * second. Same shape `Countdown` and `LotCard` take.
   */
  nowServerMs: () => number;
  /**
   * The connection is down, so the countdown is not to be trusted. Shown as a
   * state rather than left ticking: a number that is quietly wrong on a closing
   * lot is worse than an admission.
   */
  offline?: boolean;
  /**
   * How many lots the catalogue below is showing, when the caller knows. Browse
   * passes its filtered count; Live Now passes how many are open.
   */
  countLabel?: string | null;
  /**
   * The close times of the lots the page is DISPLAYING.
   *
   * 🔴 THE SALE'S OWN `ends_at` IS OFTEN NULL. A staggered timed sale closes
   * when its lots close and the house never types a sale-level time, so these
   * are the only source for when closing BEGINS. `saleClosingBeginsAt` takes
   * the earliest of them, and refuses to when the house hid its close time.
   *
   * Taken from the displayed rows rather than a stored aggregate, so the header
   * cannot state a time the catalogue disagrees with.
   */
  lotEndsAt?: readonly (string | null)[];
}

export function SaleHeader({
  sale,
  houseName,
  nowServerMs,
  offline = false,
  countLabel = null,
  lotEndsAt = [],
}: SaleHeaderProps) {
  const [termsOpen, setTermsOpen] = useState(false);

  // The sale's own clock. Resolved once so every time in the band agrees, and
  // falling back rather than throwing — `Intl` throws on an unknown zone, which
  // would take the whole header down over a typo in one column.
  const saleZone = resolveTimeZone(sale.timezone);

  /*
   * 🔴 THE CALENDAR ENTRY IS FOR WHEN CLOSING BEGINS, NOT WHEN IT ENDS, AND
   * THERE IS EXACTLY ONE OF THEM. Jim, 2026-09-18: *"You can make for the time
   * lots begin to close, but I don't want per lot calendar reminders."* In a
   * staggered sale the first and last gavel are hours apart, and an entry on
   * the last would have a bidder arrive after most of the sale had gone.
   *
   * ⚠️ NO BUTTON ON A LOT, ANYWHERE. A 200-lot sale would otherwise offer 200
   * calendar entries, which is not a feature — it is a way to make somebody's
   * diary unusable and their phone buzz all afternoon. Guarded by a test.
   *
   * Null — and so no button — whenever the house hid its close time, because
   * `saleClosingBeginsAt` refuses to derive around that flag.
   */
  const closingBeginsAt = saleClosingBeginsAt(sale, lotEndsAt);

  /*
   * 🔴 ONLY BEFORE IT HAPPENS. Found by LOOKING at the rendered band rather than
   * at the code: on a sale already closing, this read "Lots begin closing Fri,
   * Sep 18, 4:50 AM" — two hours in the past — and offered to put it in a
   * diary. A reminder for a moment that has gone is worse than no reminder,
   * which is the same principle that gates the pickup button on a real date.
   * Once closing has started, the live "last lot closes" countdown beside this
   * is the only useful fact, so the whole row stands down.
   */
  const closingBegun = closingBeginsAt !== null && Date.parse(closingBeginsAt) <= nowServerMs();
  const closingEvent: CalendarEvent | null =
    closingBeginsAt === null || closingBegun
      ? null
      : {
          title: `${sale.title} — lots begin closing`,
          startsAt: closingBeginsAt,
          // No end: the platform knows when closing STARTS, and anti-snipe means
          // nobody knows when it finishes. A guessed end would be a made-up fact
          // in a calendar, so the default short block is used instead.
          endsAt: null,
          location: pickupLine(sale),
          description: [
            `${houseName} · ${String(sale.lot_count)} lots`,
            `Lots start closing ${formatInZone(closingBeginsAt, saleZone)}.`,
            'Lots close in staggered order, not all at once.',
          ].join('\n\n'),
          url: typeof window === 'undefined' ? null : window.location.href,
        };
  const premium = premiumPercent(sale.buyers_premium_bps);
  const blurb = (sale.description ?? '').trim();
  const preview = previewWindow(sale);
  const where = pickupLine(sale);

  /*
   * 🔴 THE BAND SAID WHERE TO COLLECT AND NOT WHEN. Jim, 2026-09-18, looking at
   * the live header: *"the collection times are not in the header."* He is
   * right, and the label made it worse — "Pickup" promises both, then answered
   * half. The times existed by then (the auction form writes them); this row
   * simply had not been told.
   *
   * 🔴 ALL OF THEM, NOT JUST THE NEXT. It listed only the soonest at first, on
   * the reasoning that a band is a summary and the terms modal has the rest.
   * Jim, same day, having added a second: *"I added a second collection time and
   * it did not render in the header field."* A house that publishes three
   * collection days is telling bidders about three, and showing one makes the
   * other two look like they were not saved.
   *
   * PAST WINDOWS STILL DROP OUT — `remainingPickupWindows` is the same rule the
   * pickup screen and the phone use for "can I still make this", so a sale that
   * collected last Tuesday does not keep advertising it.
   */
  const collectionTimes = useMemo(() => {
    const zoned = (iso: string, withZone: boolean) =>
      formatInZone(iso, saleZone, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        withZone,
      });

    return remainingPickupWindows(parsePickupWindows(sale.pickup_dates), nowServerMs()).map((w) => {
      // The zone rides on the END where there is one, so it lands once at the
      // close of the phrase rather than in the middle of it.
      if (w.endsAt === null) return zoned(w.startsAt, true);
      return `${zoned(w.startsAt, false)} – ${formatInZone(w.endsAt, saleZone, { hour: 'numeric', minute: '2-digit' })}`;
    });
  }, [sale.pickup_dates, saleZone, nowServerMs]);

  return (
    <div className="w-salehead" data-testid="sale-header">
      <div className="w-salehead-main">
        <div className="w-salehead-tags">
          {offline ? (
            <span className="w-pill">Not updating</span>
          ) : (
            <span className="w-pill">
              <span className="w-dot" />
              {sale.status === 'active' ? 'Live' : 'Preview'}
            </span>
          )}
          {countLabel !== null ? <span className="w-lbl">{countLabel}</span> : null}
        </div>

        <h1 className="w-h2 w-salehead-title">{sale.title}</h1>

        {/* The house's own words about the sale. Clamped to two lines: the full
            text is in the modal, and a band that grows with a long description
            pushes the catalogue off the screen. */}
        {blurb !== '' ? (
          <p className="w-salehead-blurb" data-testid="sale-header-blurb">
            {blurb}
          </p>
        ) : null}

        {/* The facts a bidder needs before they bid, in the order they need
            them: when closing starts, when they can look at it, where and when
            they collect.
            🔴 NO SALE-LEVEL CLOSE COUNTDOWN. Jim, 2026-09-18, trimming this
            band: *"remove the 'Last Lot Closes (time)'"*. Every lot card below
            carries its own countdown, so the band was restating the least
            useful version of it — the moment the sale is already over. "Lots
            begin closing" is the timing a bidder plans around and it stays. */}
        <div className="w-salehead-meta">
          {closingEvent !== null ? (
            <span>
              <span className="w-lbl">Lots begin closing</span>
              <b>
                {formatInZone(closingEvent.startsAt, saleZone, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </b>
            </span>
          ) : null}

          {preview !== null ? (
            <span>
              <span className="w-lbl">Preview</span>
              <b>{previewLabel(preview.startsAt, preview.endsAt, saleZone)}</b>
            </span>
          ) : null}

          {/* Place before time (Jim, 2026-09-18). A bidder checks whether they
              can get there at all before they check which day. */}
          {where !== null ? (
            <span>
              <span className="w-lbl">
                {collectionTimes.length === 0 ? 'Pickup' : 'Collect at'}
              </span>
              <b>{where}</b>
            </span>
          ) : null}

          {collectionTimes.length > 0 ? (
            <span>
              <span className="w-lbl">Collection</span>
              {/* 🔴 STACKED, SOONEST AT THE TOP. Jim, 2026-09-18: *"Additional
                  Collection times should stack vertically with the soonest at
                  the top and descending by distance."* Run together on one line
                  they read as a single long sentence and a bidder has to parse
                  where one day ends and the next begins; stacked, each is a
                  thing they can pick.

                  The ORDER needs nothing here: `parsePickupWindows` sorts
                  earliest-first as it reads the column, and
                  `remainingPickupWindows` only filters. Sorting again in the
                  component would be a second opinion about the same list. */}
              <span className="w-salehead-stack">
                {collectionTimes.map((t) => (
                  <b key={t}>{t}</b>
                ))}
              </span>
            </span>
          ) : null}

          {/* 🔴 NO LOT COUNT HERE. Jim, 2026-09-18: *"Remove 'Lots (#)'"*. It
              is not lost — the tag row at the top of this band already says
              "5 lots open", which is the more useful number anyway: how many a
              bidder can still bid on, rather than how many the sale contains. */}
        </div>
      </div>

      {/* 🔴 THE PREMIUM SITS IN THE CORNER, NOT IN THE LIST. Jim, 2026-09-18:
          *"Move the 'Buyer's Premium' to the top right corner of the field."*
          It is the one fact in this band that is about MONEY rather than about
          time or place, and buried mid-list it read as another logistics detail.
          In the corner it is the first thing the eye lands on after the title,
          which is where a cost belongs.

          It shares this column with the terms button because they are the two
          things a bidder reaches for before committing — what it costs, and
          what else they are agreeing to. */}
      <div className="w-salehead-aside">
        {premium !== null ? (
          <span className="w-salehead-premium">
            <span className="w-lbl">Buyer&rsquo;s premium</span>
            <b data-testid="sale-header-premium">{premium}</b>
          </span>
        ) : null}

        {/* 🔴 THE FOURTH CORNER. Jim, 2026-09-18: *"Move the Add to Calendar
            button to the bottom right corner of the field."* Inline beside the
            closing time it competed with the time itself — a control sitting in
            the middle of a sentence of facts. In the corner it is a thing you
            DO, matching the terms button above it, and the band's four corners
            each hold one item.

            Out of flow for the same reason the premium is: the terms button
            centres on the whole band only if nothing above or below it takes
            part in that centring. */}
        {closingEvent !== null ? (
          <div className="w-salehead-cal">
            <AddToCalendar event={closingEvent} label="Add to calendar" />
          </div>
        ) : null}

        {/* Opens the modal that already holds the description, the premiums, the
            approval and card rules, the preview dates and the removal prose. */}
        <button
          type="button"
          className="w-btn w-btn-out w-btn-sm w-salehead-btn"
          onClick={() => {
            setTermsOpen(true);
          }}
        >
          Sale details &amp; terms
        </button>
      </div>

      {termsOpen ? (
        <SaleTermsModal
          auction={sale}
          houseName={houseName}
          browseTo={null}
          onClose={() => {
            setTermsOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
